// Map an FFTBG unit's Class (+ Gender) to a disc-derived sprite atlas key + a
// palette index — the data behind the walking billboards on the map
// (MapViewer3D, used by the bracket-hover popup and the Maps page).
//
// Sprite keys are the romaji disc filenames (KNIGHT_M, YUMI_M = Archer, KURO =
// Black Mage, SIRO = White Mage…); the authoritative slot→key table lives in
// tools/fft-sprite/gen_shapes.mjs. FFTBG class names are the PSX/WotL set
// (Wizard = Black Mage, Priest = White Mage, Lancer = Dragoon, Calculator,
// Mediator, Oracle…) and can arrive SPACED ("Time Mage", "Red Chocobo"), so we
// strip whitespace to match the unspaced canonical keys (the same convention as
// lib/unitStats.js's classKey).
//
// Palettes: each SPR carries 16 (manifest `pals`), the first 8 being the FFTBG
// team colors (confirmed from the dev's canonical sprites): index 0 Black ·
// 1 Blue · 2 Red · 3 Green · 4 White · 5 Purple · 6 Yellow · 7 Brown. HUMANS tint
// by their team color name via teamPalette(). For MONSTERS, color is a family
// tier (Wild Boar / Swine / Porky are all URI in different palettes) and is
// INDEPENDENT of team. The per-class tier is now pinned AUTHORITATIVELY from the
// disc: FFT's 48 monster jobs (0x5E–0x8D) are 16 families of 3 CONSECUTIVE jobs
// sharing one sprite, and the engine renders tier = palette = the job's rank in
// its family (0/1/2). So `pal` below = the class's position within its family,
// in the game's job order (= the FFTBG class-list order in classes.backup.json).
// Cross-checked: this reproduces the BOM family (Bomb 0 / Grenade 1 / Explosive 2)
// that NOTES.md had RE'd independently by eyeballing colors. (Unmapped classes
// still fall back to stablePal — see spriteForUnit.) RE script: tools/fft-sprite/
// reMonsterPalettes.mjs.

// human (space-stripped) class name → sprite base; the gender suffix (_M/_W) is
// added in spriteForUnit. Covers all 20 generic FFTBG jobs.
export const HUMAN_SPRITE = {
  Squire: 'MINA', Chemist: 'ITEM', Knight: 'KNIGHT', Archer: 'YUMI',
  Monk: 'MONK', Priest: 'SIRO', Wizard: 'KURO', TimeMage: 'TOKI',
  Summoner: 'SYOU', Thief: 'THIEF', Mediator: 'WAJU', Oracle: 'ONMYO',
  Geomancer: 'FUSUI', Lancer: 'RYU', Samurai: 'SAMU', Ninja: 'NINJA',
  Calculator: 'SAN', Bard: 'GIN', Dancer: 'ODORI', Mime: 'MONO',
};

// gender-locked jobs ship a single sheet, regardless of the unit's Gender field.
const FORCE_GENDER = { GIN: 'M', ODORI: 'W' };   // Bard ♂, Dancer ♀

// monster (space-stripped) class name → { key, pal }. Families of three are
// palette swaps of ONE model; `pal` = the tier index 0/1/2 (the job's rank in its
// family — see the header note; RE'd from the disc, authoritative). null = no
// pinned tier → stablePal fallback.
export const MONSTER_SPRITE = {
  // Chocobo family
  Chocobo: { key: 'CYOKO', pal: 0 }, BlackChocobo: { key: 'CYOKO', pal: 1 }, RedChocobo: { key: 'CYOKO', pal: 2 },
  // Goblin family
  Goblin: { key: 'GOB', pal: 0 }, BlackGoblin: { key: 'GOB', pal: 1 }, Gobbledeguck: { key: 'GOB', pal: 2 },
  // Bomb family
  Bomb: { key: 'BOM', pal: 0 }, Grenade: { key: 'BOM', pal: 1 }, Explosive: { key: 'BOM', pal: 2 },
  // Panther / Coeurl family (HYOU)
  RedPanther: { key: 'HYOU', pal: 0 }, Coeurl: { key: 'HYOU', pal: 1 }, Vampire: { key: 'HYOU', pal: 2 },
  // Pisco Demon / Squid family (IKA)
  PiscoDemon: { key: 'IKA', pal: 0 }, Squidraken: { key: 'IKA', pal: 1 }, Mindflayer: { key: 'IKA', pal: 2 },
  // Skeleton family (SUKERU)
  Skeleton: { key: 'SUKERU', pal: 0 }, Draugr: { key: 'SUKERU', pal: 1 }, Reaper: { key: 'SUKERU', pal: 2 },
  // Ghost family (YUREI)
  Ghoul: { key: 'YUREI', pal: 0 }, Ghost: { key: 'YUREI', pal: 1 }, Revenant: { key: 'YUREI', pal: 2 },
  // Bird family (TORI, flying)
  Juravis: { key: 'TORI', pal: 0 }, IronHawk: { key: 'TORI', pal: 1 }, Cockatrice: { key: 'TORI', pal: 2 },
  // Pig / Boar family (URI) — Swine / Porky / Wild Boar (game's job order)
  Swine: { key: 'URI', pal: 0 }, Porky: { key: 'URI', pal: 1 }, WildBoar: { key: 'URI', pal: 2 },
  // Minotaur family (MINOTA)
  BullDemon: { key: 'MINOTA', pal: 0 }, Minotaur: { key: 'MINOTA', pal: 1 }, Sekhret: { key: 'MINOTA', pal: 2 },
  // Malboro family (MOL)
  Malboro: { key: 'MOL', pal: 0 }, Ochu: { key: 'MOL', pal: 1 }, GreatMalboro: { key: 'MOL', pal: 2 },
  // Behemoth family (BEHI)
  Behemoth: { key: 'BEHI', pal: 0 }, KingBehemoth: { key: 'BEHI', pal: 1 }, DarkBehemoth: { key: 'BEHI', pal: 2 },
  // Dragon family (DORA1)
  Dragon: { key: 'DORA1', pal: 0 }, BlueDragon: { key: 'DORA1', pal: 1 }, RedDragon: { key: 'DORA1', pal: 2 },
  // Hydra family (DORA2) — Wyvern / Hydra / Tiamat
  Wyvern: { key: 'DORA2', pal: 0 }, Hydra: { key: 'DORA2', pal: 1 }, Tiamat: { key: 'DORA2', pal: 2 },
  // Demon family (DEMON) — only 2 tiers exist on this sprite.
  ArchaicDemon: { key: 'DEMON', pal: 0 }, UltimaDemon: { key: 'DEMON', pal: 1 },
  // Apanda shares Byblos's sprite (BIBUROS, slot 0x96): Byblos is its purple palette 0, Apanda the grey-and-gold 1.
  // (It had been drawn as an Archaic Demon stand-in until 2026-10-23.)
  Apanda: { key: 'BIBUROS', pal: 1 },
  // Ahriman / floating-eye family (AHRIMAN) — extracted from the dev's BMP set
  FloatingEye: { key: 'AHRIMAN', pal: 0 }, Ahriman: { key: 'AHRIMAN', pal: 1 }, Plague: { key: 'AHRIMAN', pal: 2 },
  // Treant / tree family (TREANT)
  Dryad: { key: 'TREANT', pal: 0 }, Treant: { key: 'TREANT', pal: 1 }, Taiju: { key: 'TREANT', pal: 2 },
  // Serpentarius — the Elidibs serpent sprite (slot 0x97); rig MON/RUKA
  Serpentarius: { key: 'ELIDIBS', pal: 0 },
  // uniques / closest model (no dedicated sheet for these) — base palette of their
  // own sprite (not a shared family tier).
  SteelGiant: { key: 'TETSU', pal: 0 }, Byblos: { key: 'BIBUROS', pal: 0 }, HolyDragon: { key: 'DORA1', pal: 0 },
};

// when a class isn't in either table
const FALLBACK_HUMAN = 'KNIGHT_M';
const FALLBACK_MONSTER = 'GOB';

const noSpace = (s) => String(s == null ? '' : s).replace(/\s+/g, '');

// stable 0–2 from a string, so a given monster class is consistently one color
// (rather than flickering between hovers).
function stablePal(name) {
  let h = 0; const s = String(name);
  for (let i = 0; i < s.length; i++) h = (Math.imul(h, 31) + s.charCodeAt(i)) >>> 0;
  return h % 3;
}

// FFTBG team color → SPR palette index. Confirmed from the dev's canonical
// sprites (each generic job carries 8 team palettes in indices 0–7). Champion
// has no palette of its own — on the field it renders in a normal team color —
// so callers substitute a non-conflicting color for it (see TEAM_COLORS).
export const TEAM_PALETTE = { black: 0, blue: 1, red: 2, green: 3, white: 4, purple: 5, yellow: 6, brown: 7 };
export const TEAM_COLORS = Object.keys(TEAM_PALETTE);

// team color name → palette index (default black/0 for unknown, incl. 'Champion')
export function teamPalette(teamColor) {
  return TEAM_PALETTE[String(teamColor || '').toLowerCase()] ?? 0;
}

export function isMonsterClass(cls) {
  return Object.prototype.hasOwnProperty.call(MONSTER_SPRITE, noSpace(cls));
}

// True when this class has a real walk sprite (a generic job or a mapped
// monster). Lets callers keep the static headshot for unmapped classes (a
// unique raid boss with no sheet) instead of rendering a wrong stand-in —
// spriteForUnit always returns SOME key, so it can't signal "fell back".
export function hasWalkSprite(cls) {
  const name = noSpace(cls);
  return Object.prototype.hasOwnProperty.call(HUMAN_SPRITE, name)
    || Object.prototype.hasOwnProperty.call(MONSTER_SPRITE, name);
}

// Resolve a unit → { key, pal }: the sprite atlas key + desired palette index.
// `teamColor` is the unit's team color NAME ('Red', 'Blue', …, or 'Champion');
// it's ignored for monsters (their color is family-based, not team-based). The
// renderer clamps `pal` to the sprite's actually-used palettes, so an
// out-of-range request degrades gracefully rather than going black.
export function spriteForUnit(cls, gender, teamColor) {
  const name = noSpace(cls);
  const human = HUMAN_SPRITE[name];
  if (human) {
    const suffix = FORCE_GENDER[human] || (gender === 'Female' ? 'W' : 'M');
    return { key: human + '_' + suffix, pal: teamPalette(teamColor) };
  }
  const mon = MONSTER_SPRITE[name];
  if (mon) return { key: mon.key, pal: mon.pal == null ? stablePal(name) : mon.pal };
  // unknown class — a Monster-gender unit gets a creature, everything else a knight
  if (gender === 'Monster') return { key: FALLBACK_MONSTER, pal: stablePal(name) };
  return { key: FALLBACK_HUMAN, pal: teamPalette(teamColor) };
}

// Parse a team's API "Palettes" string ("Purple/Yellow") → [primary, alt] (lowercase).
export function paletteOptions(palettesStr) {
  return String(palettesStr || '').split('/').map((c) => c.trim().toLowerCase()).filter(Boolean);
}

// Resolve the two combatants' display colors. Each team has a primary + an alt
// (its API `palettes`; a regular team's primary is just its name). The CHAMPION
// has no fixed color, so when its primary would clash with the opponent it takes
// its alt — so the two teams never share a color. Returns [leftColor, rightColor].
// `left`/`right` are team objects { name, palettes } (either may be null).
export function matchColors(left, right) {
  const opts = (t) => {
    const o = paletteOptions(t && t.palettes);
    if (o.length) return o;
    const n = t && t.name;
    return n && n !== 'champion' ? [String(n).toLowerCase()] : [];
  };
  const lo = opts(left), ro = opts(right);
  let l = lo[0], r = ro[0];
  if (l && l === r) {                                 // clash → champion yields, else the right side does
    if (left && left.name === 'champion' && lo[1]) l = lo[1];
    else if (ro[1]) r = ro[1];
    else if (lo[1]) l = lo[1];
  }
  l = l || 'black';
  return [l, r || (l !== 'blue' ? 'blue' : 'red')];
}

// Tag a map's start tiles with the sprite of the unit standing there. Player 1 =
// the left team, Player 2 = the right; `start.unit` indexes that team's roster.
// Teams are { name, roster, palettes } and either may be null (→ a bare start,
// which the viewer renders as a default Knight). Shared by the bracket hover and
// the Fight View so both place the real units in their ENTD start positions.
export function startsWithSprites(starts, leftTeam, rightTeam) {
  if (!starts || !starts.length) return starts || [];
  const [lColor, rColor] = matchColors(leftTeam, rightTeam);
  const L = (leftTeam && leftTeam.roster) || [], R = (rightTeam && rightTeam.roster) || [];
  return starts.map((s) => {
    const p1 = s.team === 'Player 1';
    const member = (p1 ? L : R)[s.unit];
    if (!member) return s;
    return { ...s, sprite: spriteForUnit(member.Class, member.Gender, p1 ? lColor : rColor) };
  });
}

// ---- Maps page: a random unit per tile (no roster to read) ----
const HUMAN_BASES = Object.values(HUMAN_SPRITE);
const MONSTER_KEYS = [...new Set(Object.values(MONSTER_SPRITE).map((m) => m.key))];

// Mostly-human random unit, occasional monster. `rng` is a 0..1 function
// (seedable via mulberry32 for stability per map). Always returns a key that
// exists in the atlas set.
export function randomSprite(rng = Math.random, monsterChance = 0.18) {
  if (rng() < monsterChance) {
    const key = MONSTER_KEYS[Math.floor(rng() * MONSTER_KEYS.length)] || FALLBACK_MONSTER;
    return { key, pal: Math.floor(rng() * 3) };
  }
  const base = HUMAN_BASES[Math.floor(rng() * HUMAN_BASES.length)] || 'KNIGHT';
  const suffix = FORCE_GENDER[base] || (rng() < 0.5 ? 'M' : 'W');
  const color = TEAM_COLORS[Math.floor(rng() * TEAM_COLORS.length)];   // any team color, for variety
  return { key: base + '_' + suffix, pal: teamPalette(color) };
}

// Small deterministic PRNG (mulberry32) → a 0..1 function, so the Maps page can
// seed per-map-number and get a stable cast each visit.
export function mulberry32(seed) {
  let a = seed >>> 0;
  return function () {
    a = (a + 0x6D2B79F5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
